import { useCallback, useMemo, useRef } from 'react';
import type { TOCNode } from '../types/DocumentTypes';

function flattenChapters(chapters: TOCNode[]): TOCNode[] {
  return chapters.flatMap((chapter) => [
    chapter,
    ...flattenChapters(chapter.children || []),
  ]);
}

export function useChapterNavigation(
  chapters: TOCNode[] | undefined,
  onNavigated?: () => void,
) {
  const flattenedChapters = useMemo(() => {
    return flattenChapters(chapters || []);
  }, [chapters]);
  const moveToChapterRef = useRef<((chapter: TOCNode) => Promise<void>) | null>(
    null,
  );

  // Callers can register the underlying reader movement once
  const registerMoveToChapter = useCallback(
    (fn: (chapter: TOCNode) => Promise<void>) => {
      moveToChapterRef.current = fn;
    },
    [],
  );

  const navigateToChapter = useCallback(
    async (chapter: TOCNode): Promise<void> => {
      if (moveToChapterRef.current) {
        await moveToChapterRef.current(chapter);
      }
      const anchorId = `b${chapter.blockId}`;
      const waitAndScroll = async (maxTries = 20, delayMs = 50) => {
        for (let i = 0; i < maxTries; i++) {
          const el = document.getElementById(anchorId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          await new Promise((r) => setTimeout(r, delayMs));
        }
      };
      await waitAndScroll();
      onNavigated?.();
    },
    [onNavigated],
  );

  const navigateToChapterByNodeId = useCallback(
    async (nodeId: number): Promise<void> => {
      if (!chapters) {
        return;
      }
      const chapter = flattenedChapters.find((c) => c.nodeId === nodeId);

      if (!chapter) {
        return;
      }
      await navigateToChapter(chapter);
    },
    [chapters, navigateToChapter],
  );

  return {
    registerMoveToChapter,
    navigateToChapter,
    navigateToChapterByNodeId,
  };
}
